/**
 * Headless presentation helpers for the Manufacturing setup surfaces
 * (design note
 * `docs/features/cost-engine-productionisation/03_manufacturing_surfaces_design.md`).
 *
 * Engine-use is a *field provenance* signal — does the engine read this
 * Tharstern field — never a validation signal (contrast
 * `cost-engine-validation-presentation.ts`'s cost-check states, which are
 * about whether the engine's answer agrees with Tharstern's).
 */

import { format } from "date-fns";
import type {
  EngineUseStatus,
  ManufacturingSyncInfo,
} from "../data/manufacturing-setup-types";

export type EngineUseTone = "success" | "neutral" | "outline";

export const ENGINE_USE_STATES: Record<
  EngineUseStatus,
  { label: string; tone: EngineUseTone; symbol: string }
> = {
  used_by_engine: { label: "Used by engine", tone: "success", symbol: "●" },
  replicated_not_used: {
    label: "Replicated, not used",
    tone: "neutral",
    symbol: "○",
  },
  not_yet_replicated: {
    label: "Not yet replicated",
    tone: "outline",
    symbol: "◌",
  },
};

export function engineUseLabel(status: EngineUseStatus): string {
  return ENGINE_USE_STATES[status].label;
}

export function engineUseTone(status: EngineUseStatus): EngineUseTone {
  return ENGINE_USE_STATES[status].tone;
}

export function engineUseSymbol(status: EngineUseStatus): string {
  return ENGINE_USE_STATES[status].symbol;
}

/** "9 of 14 fields used" — the list page's Engine use summary column. */
export function formatEngineUseSummary(used: number, total: number): string {
  return `${used} of ${total} ${total === 1 ? "field" : "fields"} used`;
}

/** "synced 03:12 from `CostCentres`" — every list and detail page's sync
 *  line. Backtick-quoted per the glossary so the Tharstern table name reads
 *  as a literal identifier, not prose. `syncedAt` is nullable on the wire
 *  (a table this snapshot hasn't recorded a sync run for yet) — renders
 *  "not yet synced", never a fabricated time. */
export function formatSyncedFrom(sync: ManufacturingSyncInfo): string {
  if (!sync.syncedAt) return `not yet synced from \`${sync.table}\``;
  return `synced ${format(new Date(sync.syncedAt), "HH:mm")} from \`${sync.table}\``;
}

/** "EXETER01" → "Exeter" — the town name a print manager actually says,
 *  for tables that sit next to another table already naming sites in
 *  plain English (e.g. Routing policy's Per-step feasible sites). Falls
 *  back to the raw code for anything unrecognised rather than guessing. */
const SITE_TOWN_NAMES: Record<string, string> = {
  EXETER01: "Exeter",
  BUCKINGHAM01: "Buckingham",
  BUCKINGHAM02: "Buckingham",
  PLYMOUTH01: "Plymouth",
};

export function siteTownName(siteCode: string): string {
  return SITE_TOWN_NAMES[siteCode] ?? siteCode;
}

/** "Changed in Tharstern 10 Sep" — a pure setup fact from change tracking,
 *  with no validation meaning (decision 12 Sep, `03_manufacturing_surfaces_
 *  design.md` "setup visibility and costing validation are separate"). */
/** `changedAt` is `null` while this table isn't on change tracking yet
 *  (cost-engine-productionisation spec 05 §9.2) — renders "not available",
 *  never a fabricated or silently omitted date. */
export function formatChangedInTharstern(
  changedAt: string | null | undefined,
): string {
  if (!changedAt) return "Changed in Tharstern — not available";
  return `Changed in Tharstern ${format(new Date(changedAt), "d MMM")}`;
}

/** "Used in 38 price checks · see rolling validation" / "Not yet used in
 *  any price check" — the setup detail page's one link-out line to rolling
 *  validation (the never-checked routing rule's own data). Zero renders as
 *  plain text; a positive count is meant to be wrapped in a link by the
 *  caller. */
export function formatPriceChecksUsedIn(count: number): string {
  if (count === 0) return "Not yet used in any price check";
  return `Used in ${count} price ${count === 1 ? "check" : "checks"} · see live costing checks`;
}

/** "“Exeter wins ties” — Ben Pollard, 11 Sep" — shown beside a
 *  Routing policy choice's current value, naming who last set it and when
 *  (item C2, `04_handover_2026-09-12.md`). Falls back to the choice's
 *  initial version for a choice that has never been changed since. */
export function formatChoiceLastChanged(
  note: string,
  changedBy: string,
  changedAt: string,
): string {
  const date = format(new Date(changedAt), "d MMM");
  return `“${note}” — ${changedBy}, ${date}`;
}
